import { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Download, Key, FileText, AlertCircle, CheckCircle, Clock, RefreshCw, Copy, Check, ArrowUpRight, ExternalLink } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { toast } from "@/components/ui/use-toast";
import { CheckboxIndicator } from "@radix-ui/react-checkbox";
import { DataTable } from "@/components/ui/data-table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface DocumentUploadStatus {
	id: string;
	status: "created" | "in_progress" | "done" | "in_error";
	created_at: string;
	updated_at: string;
	user_id: string;
	entity?: string;
	entity_uuid?: string;
	entity_name?: string;
    documents : {
        name: string;
        id: string;
    }
}

export const DocumentUploadTab = () => {
    
	const { t } = useTranslation();
	const [uploadKey, setUploadKey] = useState<string | null>(null);
	const [isGeneratingKey, setIsGeneratingKey] = useState(false);
	const [documents, setDocuments] = useState<DocumentUploadStatus[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [isCopied, setIsCopied] = useState(false);
	const fetchDocuments = async () => {
		setIsLoading(true);
		setError(null);
		
		try {
			// First fetch documents
			const { data, error } = await supabase
				.from("document_upload_status")
				.select(`
                    *,
                    documents (
                        *
                    )
                `)
				.order("created_at", { ascending: false });

            console.log("Fetched documents:", data);
				
			if (error) {
				throw error;
			}

			// For completed documents with entity and entity_uuid, fetch entity names
			if (data && data.length > 0) {
				const docsWithEntityNames = await Promise.all(
					data.map(async (doc) => {
						// Only fetch names for completed documents with entity info
						if (doc.status === "done" && doc.entity && doc.entity_uuid) {
							try {
								// Fetch entity name based on entity type
								if (doc.entity === "candidate") {
									const { data: candidateData } = await supabase
										.from("candidates")
										.select("name")
										.eq("id", doc.entity_uuid)
										.single();
									
									return { ...doc, entity_name: candidateData?.name || 'Unknown Candidate' };
								} else if (doc.entity === "job") {
									const { data: jobData } = await supabase
										.from("jobs")
										.select("title")
										.eq("id", doc.entity_uuid)
										.single();
									
									return { ...doc, entity_name: jobData?.title || 'Unknown Job' };
								}
							} catch (entityError) {
								console.error(`Error fetching ${doc.entity} info:`, entityError);
							}
						}
						return doc;
					})
				);
				
				setDocuments(docsWithEntityNames);
			} else {
				setDocuments(data || []);
			}
		} catch (err) {
			console.error("Error fetching documents:", err);
			setError(t("settings.documentUpload.error.fetchFailed"));
		} finally {
			setIsLoading(false);
		}
	};
	
	// Navigate to the entity page
	const navigateToEntity = (entity: string | undefined, entityUuid: string | undefined) => {
		if (!entity || !entityUuid) return;
		
		const entityRoutes = {
			candidate: `/candidates/${entityUuid}`,
			job: `/jobs/${entityUuid}`
		};
		
		const route = entityRoutes[entity as keyof typeof entityRoutes];
		if (route) {
			window.location.href = route;
		}
	};

	useEffect(() => {
		fetchDocuments();
	}, []);

	const generateUploadKey = async () => {
		setIsGeneratingKey(true);
		setError(null);
		try {
			// Call to Supabase Edge Function
			const { data, error } = await supabase.functions.invoke('create-upload-key', {
				method: 'POST',
			});
			
			if (error) throw error;

            console.log("Generated upload key:", data);

            if (!data || !JSON.parse(data).key) {
                throw new Error(t("settings.documentUpload.error.keyGeneration"));
            }
			
			setUploadKey(JSON.parse(data).key);
			
			toast({
				title: t("settings.documentUpload.success.title"),
				description: t("settings.documentUpload.success.keyGenerated"),
				duration: 3000,
			});

		} catch (err) {
			console.error("Error generating upload key:", err);
			setError(t("settings.documentUpload.error.keyGeneration"));
		} finally {
			setIsGeneratingKey(false);
		}
	};
	const downloadClient = (os: 'windows' | 'mac') => {
		// Use OS-specific download paths
		const downloadPath = os === 'windows' 
			? "/Digger-Document-Upload.exe" 
			: "/document-uploader/client/mac";
		window.open(downloadPath, "_blank");
	};

	const getStatusIcon = (status: string) => {
		switch (status) {
			case "created":
				return <Clock className="h-4 w-4 text-gray-500" />;
			case "in_progress":
				return <Clock className="h-4 w-4 text-blue-500" />;
			case "done":
				return <CheckCircle className="h-4 w-4 text-green-500" />;
			case "in_error":
				return <AlertCircle className="h-4 w-4 text-red-500" />;
			default:
				return <FileText className="h-4 w-4" />;
		}
	};

	const getStatusText = (status: string) => {
		return t(`settings.documentUpload.status.${status}`);
	};

	return (
		<>
			<Card>
				<CardHeader className="pb-3">
					<CardTitle className="text-xl flex items-center">
						<FileText className="mr-2 h-5 w-5" />
						{t("settings.documentUpload.title")}
					</CardTitle>
					<CardDescription>
						{t("settings.documentUpload.description")}
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-6">
					<Alert className="mb-6">
						<FileText className="h-4 w-4" />
						<AlertTitle>{t("settings.documentUpload.howToTitle")}</AlertTitle>
						<AlertDescription>
							<ol className="list-decimal pl-5 space-y-2 mt-2">
								<li>{t("settings.documentUpload.step1")}</li>
								<li>{t("settings.documentUpload.step2")}</li>
								<li>{t("settings.documentUpload.step3")}</li>
							</ol>
						</AlertDescription>
					</Alert>

					<div className="flex flex-wrap gap-4">
						<Button 
							onClick={generateUploadKey} 
							disabled={isGeneratingKey}
							className="flex items-center"
						>
							<Key className="mr-2 h-4 w-4" />
							{isGeneratingKey 
								? t("settings.documentUpload.generatingKey") 
								: t("settings.documentUpload.generateKey")}
						</Button>
						
						<Button 
							onClick={() => downloadClient('windows')} 
							variant="outline"
							className="flex items-center"
						>
							<Download className="mr-2 h-4 w-4" />
							{t("settings.documentUpload.downloadClientWindows")}
						</Button>

						<Button 
							onClick={() => downloadClient('mac')} 
							variant="outline"
							className="flex items-center"
                            disabled
						>
							<Download className="mr-2 h-4 w-4" />
							{t("settings.documentUpload.downloadClientMac")}
						</Button>
					</div>

                    {uploadKey && (
						<div className="mt-4 p-4 bg-muted rounded-md">
							<p className="text-sm font-medium mb-2">{t("settings.documentUpload.yourKey")}:</p>
							<div className="flex items-center justify-between">
								<code className="text-sm bg-background p-2 rounded">{uploadKey}</code>
								<Button 
									variant="ghost" 
									size="sm"
									onClick={() => {
										navigator.clipboard.writeText(uploadKey);
										setIsCopied(true);
										toast({
											description: t("settings.documentUpload.keyCopied"),
											duration: 2000,
										});
										setTimeout(() => setIsCopied(false), 2000);
									}}
									className="flex items-center"
								>
									{isCopied ? (
										<Check className="h-4 w-4 mr-2" />
									) : (
										<Copy className="h-4 w-4 mr-2" />
									)}
									{isCopied 
										? t("settings.documentUpload.copied") 
										: t("settings.documentUpload.copyKey")}
								</Button>
							</div>
							<p className="text-xs text-muted-foreground mt-2">
								{t("settings.documentUpload.keyWarning")}
							</p>
						</div>
					)}					<div className="mt-8">						
                        
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-medium">
                                {t("settings.documentUpload.uploadedDocuments")}
                            </h3>
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={fetchDocuments}
                                className="flex items-center gap-2"
                            >
                                <RefreshCw className="h-4 w-4" />
                                {t("common.refresh")}
                            </Button>
                        </div>

						{error && (
							<Alert variant="destructive" className="mb-4">
								<AlertCircle className="h-4 w-4" />
								<AlertTitle>{t("settings.documentUpload.error.title")}</AlertTitle>
								<AlertDescription>{error}</AlertDescription>
							</Alert>
						)}						<DataTable
							data={documents}
							columns={[
								{
									id: "document",
									header: t("settings.documentUpload.table.document"),
									accessorKey: "documents",
									cell: (doc) => (
										<div className="font-medium">{doc.documents.name}</div>
									),
									sortable: true,
								},
								{
									id: "status",
									header: t("settings.documentUpload.table.status"),
									accessorKey: "status",
									cell: (doc) => (
										<div className="flex items-center space-x-2">
											{getStatusIcon(doc.status)}
											<span>{getStatusText(doc.status)}</span>
										</div>
									),
									sortable: true,
								},
								{
									id: "entity",
									header: t("settings.documentUpload.table.entity"),
									accessorKey: "entity",
									cell: (doc) => (
										<div>
											{doc.status === "done" && doc.entity && (
												<div className="flex flex-col">
													<span className="font-medium">
														{doc.entity_name || (doc.entity === "candidate" 
															? t("common.candidate") 
															: t("common.job"))}
													</span>
													<span className="text-xs text-muted-foreground">
														{doc.entity === "candidate" 
															? t("common.candidate") 
															: t("common.job")}
													</span>
												</div>
											)}
										</div>
									),
									sortable: false,
								},
								{
									id: "date",
									header: t("settings.documentUpload.table.date"),
									accessorKey: "created_at",
									cell: (doc) => (
										new Date(doc.created_at).toLocaleString()
									),
									sortable: true,
								},
								{
									id: "actions",
									header: t("common.actions"),
									accessorKey: "id", // Added the required accessorKey property
									cell: (doc) => (
										<div className="flex items-center justify-end">
											{doc.status === "done" && doc.entity && doc.entity_uuid && (
												<TooltipProvider>
													<Tooltip>
														<TooltipTrigger asChild>
															<Button
																variant="ghost"
																size="sm"
																onClick={() => navigateToEntity(doc.entity, doc.entity_uuid)}
																className="flex items-center gap-1"
															>
																<span>{t("settings.documentUpload.viewEntity")}</span>
																<ExternalLink className="h-3 w-3" />
															</Button>
														</TooltipTrigger>
														<TooltipContent>
															{t("settings.documentUpload.goTo")} {doc.entity === "candidate" 
																? t("common.candidate") 
																: t("common.job")}
														</TooltipContent>
													</Tooltip>
												</TooltipProvider>
											)}
										</div>
									),
									sortable: false,
								},
 							]}
							isLoading={isLoading}
							loadingContent={
								<div className="flex items-center justify-center">
									<RefreshCw className="h-4 w-4 animate-spin mr-2" />
									{t("settings.documentUpload.loading")}
								</div>
							}
							emptyContent={t("settings.documentUpload.noDocuments")}
							pageSize={15}
						/>
					</div>
				</CardContent>
			</Card>
		</>
	);
};
